import * as React from "react";
import { useState } from "react";

interface OverviewProps {
  abstractTncLink: string;
  logo: string;
  abstractSubmissionDeadline?: string;
  onNext: () => void;
}

const Overview: React.FC<OverviewProps> = ({
  abstractTncLink,
  logo,
  abstractSubmissionDeadline,
  onNext,
}) => {
  const [showFullGuideline, setShowFullGuideline] = useState(false);

  const handleCreateButton = () => {
    onNext();
  };

  return (
    <>
      {/* Conference Card */}
      <div className="-mt-20 bg-white border border-blue-200 rounded-lg flex items-center justify-between px-6 py-4 w-full h-32">
        <div className="flex space-x-5 items-center h-full">
          <img
            src={logo}
            alt="Conference Logo"
            className="w-20 h-20 object-contain"
          />

          <div className="flex flex-col justify-center">
            <p className="text-sm font-normal leading-5 text-gray-600">
              Conference
            </p>
            <p className="text-2xl font-bold leading-8 text-gray-900">
              MSN 2026
            </p>
          </div>
        </div>
      </div>
      <div className="bg-white relative w-full">
        <div className="flex flex-col space-y-10 items-center w-full">
          {/* Submit Abstract Section */}
          <div className="flex flex-col space-y-10 items-start w-full ">
            <div className="bg-white border-2 border-blue-200 rounded-lg flex flex-col md:flex-row justify-between space-x-5 items-start p-6 w-full">
              <div className="flex flex-col space-y-5 grow">
                <div className="flex flex-col space-y-2 w-full">
                  <p className="text-lg font-bold leading-8 text-gray-900">
                    Submit Abstract
                  </p>
                  <div className="text-sm font-normal leading-5 text-gray-600">
                    {/* <p className="mb-0">
                      Each presenter may submit ONE abstract only.
                    </p> */}
                    <p>
                      You can only submit your final presentation after
                      registering for the conference.
                    </p>
                  </div>
                </div>
                <p className="text-sm font-bold leading-5 text-blue-900">
                  Submission Deadline: {abstractSubmissionDeadline || "TBA"}
                </p>
                {abstractTncLink && (
                  <p className="text-xs font-normal leading-5 text-gray-600">
                    Click here for
                    <span className="text-blue-900 underline">
                      {" "}
                      <a
                        href={abstractTncLink}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Abstract Submission Terms and Conditions
                      </a>
                    </span>
                  </p>
                )}
              </div>

              <button
                onClick={handleCreateButton}
                className="mt-6 md:mt-0 w-full md:w-fit bg-blue-900 rounded flex justify-center items-center px-5 py-2 cursor-pointer hover:bg-blue-800 transition-colors"
              >
                <div className="flex space-x-2 items-center">
                  <div className="w-6 h-6">
                    <svg
                      viewBox="0 0 25 25"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-full h-full"
                    >
                      <path
                        d="M12.5 5v15M5 12.5h15"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>
                  <p className="text-sm font-normal leading-6 text-white whitespace-nowrap">
                    Create New Abstract
                  </p>
                </div>
              </button>
            </div>

            {/* Submission Guideline */}
            <div className="bg-white border-2 border-blue-200 rounded-lg flex flex-col space-y-5 items-start p-6 w-full">
              <div className="flex flex-col space-y-2 w-full">
                <h5 className="text-lg font-bold leading-8 text-gray-900 mb-2">
                  Submission Guideline
                </h5>
                <div
                  className={`text-sm font-normal leading-5 text-gray-600 text-justify ${
                    showFullGuideline ? "" : "h-24 overflow-hidden"
                  }`}
                >
                  <div className="mb-0">
                    <h6>Abstract Submission Guidelines:</h6>
                    <ol>
                      <li>
                        Abstract must be submitted online via the Congress
                        website in English
                      </li>
                      <li>
                        Final forms of presentation can be in any of the
                        following format:
                      </li>
                      <ul style={{ listStyleType: "none", paddingLeft: 0 }}>
                        <li
                          style={{ paddingLeft: "1.5em", textIndent: "-1.5em" }}
                        >
                          » Oral Presentation
                        </li>
                        <li
                          style={{ paddingLeft: "1.5em", textIndent: "-1.5em" }}
                        >
                          » E-Poster / Poster Presentation
                        </li>
                      </ul>
                      <li>
                        Category: Select the category which BEST describes the
                        content of your abstract.
                      </li>
                      <li>
                        Categories are used for reviewing and indexing purposes:
                      </li>
                      <ul style={{ listStyleType: "none", paddingLeft: 0 }}>
                        <li
                          style={{ paddingLeft: "1.5em", textIndent: "-1.5em" }}
                        >
                          » Clinical
                        </li>
                        <li
                          style={{ paddingLeft: "1.5em", textIndent: "-1.5em" }}
                        >
                          » Non-Clinical
                        </li>
                      </ul>
                      <li>Tables, graphs and images are not permitted.</li>
                      <li>
                        Abstract submitted by fax, email or post will not be
                        accepted.
                      </li>
                      <li>
                        Abstract must be submitted on or before 5 May 2026. The
                        online submission system will close at 11:59 PM (GMT+8)
                        on the same day.
                      </li>
                      <li>
                        Abstract must be received by the announced deadline.
                        Abstract received after the deadline will not be
                        considered.
                      </li>
                      <li>
                        Please do not submit multiple copies of the same
                        abstract.
                      </li>
                    </ol>

                    <p className="mt-4">
                      The abstract should be as informative as possible and
                      include the following details:
                    </p>
                    <ul style={{ listStyleType: "none", paddingLeft: 0 }}>
                      <li
                        style={{ paddingLeft: "1.5em", textIndent: "-1.5em" }}
                      >
                        ✓ Abstract title: Limited to 25 words in UPPER CASE
                      </li>
                      <li
                        style={{ paddingLeft: "1.5em", textIndent: "-1.5em" }}
                      >
                        ✓ Abstract should be accompanied by the authors' names
                        and affiliations.
                      </li>
                      <li
                        style={{ paddingLeft: "1.5em", textIndent: "-1.5em" }}
                      >
                        ✓ Abstract text: Limited to 300 words (NOT including
                        Authors and Affiliations)
                      </li>
                      <li
                        style={{ paddingLeft: "1.5em", textIndent: "-1.5em" }}
                      >
                        ✓ Abstract topic: Abstract must be allocated to a
                        specific area of interest from below: *Adult Neurology
                        *Basic Neuroscience *Stroke
                      </li>
                      <li
                        style={{ paddingLeft: "1.5em", textIndent: "-1.5em" }}
                      >
                        ✓ Use only standard abbreviations. Special or unusual
                        abbreviations are not recommended.
                      </li>
                      <li
                        style={{ paddingLeft: "1.5em", textIndent: "-1.5em" }}
                      >
                        ✓ If otherwise necessary, the latter should be placed in
                        parentheses after the first mentioned of the full word.
                      </li>
                      <li
                        style={{ paddingLeft: "1.5em", textIndent: "-1.5em" }}
                      >
                        ✓ All abstracts must be submitted and presented in clear
                        English with accurate grammar and spelling of a quality
                        suitable for publication.
                      </li>
                      <li
                        style={{ paddingLeft: "1.5em", textIndent: "-1.5em" }}
                      >
                        ✓ Please contact{" "}
                        <a
                          className="underline"
                          href="mailto:abstract@msn2026.com"
                        >
                          abstract@msn2026.com
                        </a>{" "}
                        for more details.
                      </li>
                    </ul>
                  </div>
                </div>
                <button
                  onClick={() => setShowFullGuideline(!showFullGuideline)}
                  className="flex space-x-2 items-center justify-center w-full cursor-pointer bg-transparent border-0 p-0"
                >
                  <p className="text-base font-normal leading-6 text-center text-gray-500">
                    {showFullGuideline ? "Show Less" : "Show More"}
                  </p>
                  <div
                    className={`w-5 h-2 transition-transform ${
                      showFullGuideline ? "transform rotate-180" : ""
                    }`}
                  >
                    <svg
                      viewBox="0 0 18 8"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-full h-full"
                    >
                      <path
                        d="M1 1l8 6 8-6"
                        stroke="#6B7280"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Overview;
